// MagicLinkLogin — handles ?token=... from the recommendation email.
// Calls POST /auth/magic, stores the returned JWT, and redirects to dashboard.

import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Container,
  CssBaseline,
  ThemeProvider,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import Brand from '../components/Brand';
import LanguageSwitcher from '../components/LanguageSwitcher';
import { raphaTheme, colors, shadows } from '../theme';

export default function MagicLinkLogin() {
  const { t } = useTranslation('rapha');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('token');

    if (!token) {
      setError(t('magicLink.error'));
      return;
    }

    fetch(`${process.env.REACT_APP_API}/auth/magic`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token }),
    })
      .then((r) => r.json())
      .then((data) => {
        if (data.statusCode && data.statusCode > 201)
          throw new Error(data.message || 'Login failed');
        localStorage.setItem(
          process.env.REACT_APP_TOKEN_KEY || '',
          data.accessToken
        );
        window.location.href = '/feed';
      })
      .catch(() => {
        setError(t('magicLink.error'));
      });
  }, [t]);

  return (
    <ThemeProvider theme={raphaTheme}>
      <CssBaseline />
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          py: 6,
          px: 2,
          backgroundImage: colors.gradHero,
        }}
      >
        <Container maxWidth="sm" sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Box sx={{ position: 'absolute', top: 24, right: 24 }}>
            <LanguageSwitcher />
          </Box>
          <Box sx={{ mb: 5 }}>
            <Brand size="lg" href="/" />
          </Box>

          <Box
            sx={{
              width: '100%',
              maxWidth: 440,
              p: { xs: 3, sm: 5 },
              backgroundColor: 'rgba(18, 20, 28, 0.8)',
              backdropFilter: 'blur(20px)',
              boxShadow: shadows.xl,
              border: `1px solid ${colors.border}`,
              borderRadius: 3,
              textAlign: 'center',
            }}
          >
            {error ? (
              <>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                  {error}
                </Typography>
                <Button
                  fullWidth
                  variant="outlined"
                  size="large"
                  href="/login"
                  sx={{ py: 1.5 }}
                >
                  {t('magicLink.backToLogin')}
                </Button>
              </>
            ) : (
              <>
                <CircularProgress sx={{ color: colors.brand, mb: 3 }} />
                <Typography variant="body1" color="text.secondary">
                  {t('magicLink.verifying')}
                </Typography>
              </>
            )}
          </Box>
        </Container>
      </Box>
    </ThemeProvider>
  );
}
